import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { validateEmail, validatePassword } from "../utils/validators";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import { Card } from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Toast from "../components/common/Toast";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../context/ThemeContext";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoginLoading, error, clearError } =
    useAuth();
  const { isDark } = useTheme();

  useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = location.state?.from?.pathname || "/dashboard";
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state?.from?.pathname]);

  useEffect(() => {
    if (error) {
      setToast({
        type: "error",
        message: error,
      });
      clearError();
    }
  }, [error, clearError]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    // Password validation (basic for login)
    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        setToast({
          type: "success",
          message: "Login successful! Redirecting...",
        });

        navigate("/dashboard");
      } else {
        // Handle specific error cases
        const errorMessage =
          result.error || "Login failed. Please try again.";

        // Check for specific error types and set field errors
        if (errorMessage.toLowerCase().includes("email")) {
          setErrors({ email: errorMessage });
        } else if (errorMessage.toLowerCase().includes("password")) {
          setErrors({ password: errorMessage });
        } else if (
          errorMessage.toLowerCase().includes("invalid credentials") ||
          errorMessage.toLowerCase().includes("incorrect")
        ) {
          setErrors({
            email: "Invalid email or password",
            password: "Invalid email or password",
          });
        } else {
          setToast({
            type: "error",
            message: errorMessage,
          });
        }
      }
    } catch (error) {
      console.error("Error:", error);

      // Handle network errors
      if (!navigator.onLine) {
        setToast({
          type: "error",
          message:
            "No internet connection. Please check your network and try again.",
        });
      } else if (
        error.name === "TypeError" &&
        error.message.includes("fetch")
      ) {
        setToast({
          type: "error",
          message: "Unable to connect to server. Please try again later.",
        });
      } else {
        setToast({
          type: "error",
          message: "An unexpected error occurred. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setToast({
      type: "info",
      message: `${provider} login coming soon!`,
    });
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        isDark
          ? "bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
          : "bg-gradient-to-br from-blue-50 via-white to-blue-50"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card
          className={`backdrop-blur-sm ${
            isDark
              ? "bg-slate-800/50 border-slate-700/50"
              : "bg-white/80 border-gray-200/50"
          }`}
        >
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <span className="text-2xl font-bold text-white">T</span>
              </motion.div>
              <h1
                className={`text-2xl font-bold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Welcome Back
              </h1>
              <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                Sign in to your trading account
              </p>
            </div>

            {/* Social Login Buttons */}
            <div className="space-y-3 mb-6">
              <Button
                variant="outline"
                className={`w-full ${
                  isDark
                    ? "text-white border-slate-600 hover:bg-slate-700"
                    : "text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => handleSocialLogin("Google")}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
              <Button
                variant="outline"
                className={`w-full ${
                  isDark
                    ? "text-white border-slate-600 hover:bg-slate-700"
                    : "text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => handleSocialLogin("Apple")}
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12.017 0C8.396 0 8.025.044 6.79.207 5.555.37 4.72.643 3.986 1.016c-.752.382-1.39.893-2.028 1.53-.637.638-1.148 1.276-1.53 2.028C.055 5.308-.218 6.143-.055 7.378c.163 1.235.207 1.606.207 5.227 0 3.621-.044 3.992-.207 5.227-.163 1.235.11 2.07.483 2.804.382.752.893 1.39 1.53 2.028.638.637 1.276 1.148 2.028 1.53.734.373 1.569.646 2.804.483 1.235-.163 1.606-.207 5.227-.207 3.621 0 3.992.044 5.227.207 1.235.163 2.07-.11 2.804-.483.752-.382 1.39-.893 2.028-1.53.637-.638 1.148-1.276 1.53-2.028.373-.734.646-1.569.483-2.804-.163-1.235-.207-1.606-.207-5.227 0-3.621.044-3.992.207-5.227.163-1.235-.11-2.07-.483-2.804-.382-.752-.893-1.39-1.53-2.028C15.134.905 14.496.394 13.744.012 13.01-.361 12.175-.634 10.94-.471 9.705-.308 9.334-.264 5.713-.264c-3.621 0-3.992.044-5.227.207C.251.08-.584.353-1.318.726c-.752.382-1.39.893-2.028 1.53-.637.638-1.148 1.276-1.53 2.028C-5.249 5.018-5.522 5.853-5.359 7.088c.163 1.235.207 1.606.207 5.227 0 3.621-.044 3.992-.207 5.227-.163 1.235.11 2.07.483 2.804.382.752.893 1.39 1.53 2.028.638.637 1.276 1.148 2.028 1.53.734.373 1.569.646 2.804.483 1.235-.163 1.606-.207 5.227-.207z" />
                </svg>
                Continue with Apple
              </Button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div
                  className={`w-full border-t ${
                    isDark ? "border-slate-600" : "border-gray-300"
                  }`}
                ></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span
                  className={`px-2 ${
                    isDark
                      ? "bg-slate-800 text-slate-400"
                      : "bg-white text-gray-500"
                  }`}
                >
                  or continue with email
                </span>
              </div>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  className={
                    isDark
                      ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }
                />
              </div>

              <div>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    error={errors.password}
                    className={`pr-10 ${
                      isDark
                        ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                      isDark
                        ? "text-slate-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {showPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.76 7.76M12 12l2.122-2.122m0 0L16.242 7.758M12 12l-2.122 2.122"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className={`rounded text-blue-500 focus:ring-blue-500 focus:ring-offset-0 ${
                      isDark
                        ? "border-slate-600 bg-slate-700"
                        : "border-gray-300 bg-white"
                    }`}
                  />
                  <span
                    className={`ml-2 text-sm ${
                      isDark ? "text-slate-300" : "text-gray-700"
                    }`}
                  >
                    Remember me
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className={`text-sm transition-colors ${
                    isDark
                      ? "text-blue-400 hover:text-blue-300"
                      : "text-blue-600 hover:text-blue-500"
                  }`}
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoginLoading || isSubmitting}
              >
                {isLoginLoading || isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            {/* Sign Up Link */}
            <div className="mt-6 text-center">
              <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className={`font-medium transition-colors ${
                    isDark
                      ? "text-blue-400 hover:text-blue-300"
                      : "text-blue-600 hover:text-blue-500"
                  }`}
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </Card>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className={`mt-6 text-center text-sm ${
            isDark ? "text-slate-400" : "text-gray-500"
          }`}
        >
          <p>
            By signing in, you agree to our{" "}
            <Link
              to="/terms"
              className={
                isDark
                  ? "text-blue-400 hover:text-blue-300"
                  : "text-blue-600 hover:text-blue-500"
              }
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              className={
                isDark
                  ? "text-blue-400 hover:text-blue-300"
                  : "text-blue-600 hover:text-blue-500"
              }
            >
              Privacy Policy
            </Link>
          </p>
        </motion.div>
      </motion.div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Login;
